import * as React from 'react'
// libs
import { useSelector } from 'react-redux'
import { formValueSelector } from 'redux-form'
// components
import { ReduxSelect } from 'components/FormElements/ReduxForm'
// view components
import Action from './components/Action'
import SetField from './components/SetField'
import Schedule from './components/CreateSchedule'
import SendEmail from './components/SendEmail'
import MakePrivate from './components/MakePrivate'
import RelatedForm from './components/RelatedForm'
import ShowFunction from './components/ShowFunction'
import Confirmation from './components/Confirmation'
import RestrictRecord from './components/RestrictRecord'
import CheckMandatory from './components/CheckMandatory'
import ResponseMessage from './components/ResponseMessage'
import EnableGroupAccess from './components/EnableGroupAccess'
import CreateRelatedForm from './components/CreateRelatedForm'
import ShowInlineFunction from './components/ShowInlineFunction'
import SupervisorHierarchy from './components/SupervisorHierarchy'
import AccessFromRelatedForm from './components/AccessFromRelatedForm'
import ScheduleTemplateSelect from './components/ScheduleTemplateSelect'
// constants
import { Forms, RISK_TRANSACTION_TYPE_OPTIONS, TRANSACTION_TYPE_OPTIONS } from 'constants/index'
// views
import { CloseIcon } from 'components/Modals/SystemProcessModal/Form/components/views'
import { FieldWrapper, Wrapper } from './views'
// types
import { SystemProcessTransactionType, SystemProcessType } from 'types/graphql'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import { required } from 'utils/validators'

interface IIncomingProps {
  field: string
  removeField: () => void
  index: number
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

const Transaction: React.FC<IIncomingProps> = ({ field, removeField, index }) => {
  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { transactionType, form, processType } = useSelector((state) => ({
    form: selector(state, `${field}.form.value`),
    processType: selector(state, 'processType'),
    transactionType: selector(state, `${field}.transactionType.value`)
  }))

  const transactionTypeOptions = React.useMemo(() => {
    if (isRiskTemplate) {
      if (processType?.value === SystemProcessType.Automation) {
        return RISK_TRANSACTION_TYPE_OPTIONS.slice(0, 2)
      }
      return RISK_TRANSACTION_TYPE_OPTIONS
    }
    return TRANSACTION_TYPE_OPTIONS
  }, [isRiskTemplate, processType])

  return (
    <Wrapper data-testid='system-process-transaction' data-entity-id={index}>
      <CloseIcon
        data-testid='system-process-remove-transaction'
        data-entity-id={index}
        onClick={removeField}
        iconSize={24}
      />
      <FieldWrapper width={'50%'}>
        <ReduxSelect
          validate={required}
          menuPortalTarget={null}
          label={'Transaction Type'}
          options={transactionTypeOptions}
          name={`${field}.transactionType`}
        />
      </FieldWrapper>

      {transactionType === SystemProcessTransactionType.ShowFunction && <ShowFunction field={field} />}
      {transactionType === SystemProcessTransactionType.ShowInlineFunction && <ShowInlineFunction field={field} />}
      {transactionType === SystemProcessTransactionType.SetField && <SetField field={field} />}
      {transactionType === SystemProcessTransactionType.ShowRelatedForm && <RelatedForm field={field} form={form} />}
      {transactionType === SystemProcessTransactionType.SendEmail && <SendEmail field={field} />}
      {transactionType === SystemProcessTransactionType.CreateAction && <Action field={field} />}
      {transactionType === SystemProcessTransactionType.CreateRiskRecurrence && <Schedule field={field} />}
      {transactionType === SystemProcessTransactionType.DisableRiskRecurrence && (
        <ScheduleTemplateSelect field={field} />
      )}
      {transactionType === SystemProcessTransactionType.EnableRiskRecurrence && (
        <ScheduleTemplateSelect field={field} />
      )}
      {transactionType === SystemProcessTransactionType.Confirmation && <Confirmation field={field} />}
      {transactionType === SystemProcessTransactionType.ResponseMessage && <ResponseMessage field={field} />}
      {transactionType === SystemProcessTransactionType.CheckMandatory && <CheckMandatory field={field} />}
      {transactionType === SystemProcessTransactionType.Restrict && <RestrictRecord field={field} />}
      {transactionType === SystemProcessTransactionType.EnableGroupAccess && <EnableGroupAccess field={field} />}
      {transactionType === SystemProcessTransactionType.EnableSupervisorHierarchy && (
        <SupervisorHierarchy field={field} />
      )}
      {transactionType === SystemProcessTransactionType.RemoveGuestAccess && <MakePrivate field={field} />}
      {transactionType === SystemProcessTransactionType.AccessFromRelatedForm && (
        <AccessFromRelatedForm field={field} />
      )}
      {transactionType === SystemProcessTransactionType.CreateRelatedForm && <CreateRelatedForm field={field} />}
    </Wrapper>
  )
}

export default Transaction
